import { loadStripe } from "@stripe/stripe-js";
import { BillingConstants, SHARED_SUBSCRIPTION_PLAN_CONTENT_BY_TYPE } from "../../../constants/billing";

export const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

export const INITIAL_PLAN_QUANTITIES: Record<string, number> = {
  [BillingConstants.SUBSCRIPTION_TYPE.SINGLE_HOME]: 0,
  [BillingConstants.SUBSCRIPTION_TYPE.ADD_RESIDENT]: 0,
  [BillingConstants.SUBSCRIPTION_TYPE.ADD_CAREGIVER]: 0,
  [BillingConstants.SUBSCRIPTION_TYPE.ADMIN_TEST]: 0,
  [BillingConstants.SUBSCRIPTION_TYPE.CAREGIVER_PLAN]: 0,
  [BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN]: 0,
  [BillingConstants.SUBSCRIPTION_TYPE.NURSE_AI_ASSESSMENT]: 0,
  [BillingConstants.SUBSCRIPTION_TYPE.FAMILY_PLAN]: 0,
  [BillingConstants.SUBSCRIPTION_TYPE.BUSINESS_VENDOR_PLAN]: 0,
};

export const FEATURE_CREDIT_PURCHASE_OPTIONS = [
  {
    featureType: BillingConstants.FEATURE_CREDIT_TYPE.NCP_GENERATION,
    purchaseParam: "ncpCredits",
    label: "Automation Credit",
    shortLabel: "Automation Credit",
    price: BillingConstants.PROVIDER_NURSE_AUTOMATION_PACK_PRICE,
    description: "1 credit can be used for 1 automation job like NCP or assessment.",
  },
];

export const ADMIN_TEST_FEATURE_CREDIT_OPTION = {
  featureType: BillingConstants.FEATURE_CREDIT_TYPE.ADMIN_TEST,
  purchaseParam: "adminTestCredits",
  label: "Admin Test Bundle",
  shortLabel: "Admin Test",
  price: BillingConstants.ADMIN_TEST_CREDIT_PRICE,
  description: "Admin-only $1 test bundle for validating one-time billing and payment flows.",
};

export const EMPTY_BILLING_ADDRESS = {
  firstName: "",
  lastName: "",
  line1: "",
  line2: "",
  city: "",
  state: "WA",
  postalCode: "",
  country: "US",
};

export const BILLING_ADDRESS_TAX_VERIFICATION_AMOUNT = 10;

export const getFallbackFeatureCreditPurchaseOptions = (
  isAdminBillingUser: boolean,
  isNurseBillingRole: boolean,
  isFixedPriceNoBundleBillingRole: boolean,
) => {
  if (isAdminBillingUser) {
    return [...FEATURE_CREDIT_PURCHASE_OPTIONS, ADMIN_TEST_FEATURE_CREDIT_OPTION];
  }
  return FEATURE_CREDIT_PURCHASE_OPTIONS;
};

export const getBillingDetailsFeatureCreditBalances = (billingDetails: any, roleType: string) => ({
  [BillingConstants.FEATURE_CREDIT_TYPE.NCP_GENERATION]: Number(
    billingDetails?.featureCreditBalances?.provider_nurse_assessment
    || billingDetails?.featureCreditBalances?.ncp_generation
    || billingDetails?.automationCreditsAvailable
    || 0
  ),
  [BillingConstants.FEATURE_CREDIT_TYPE.ADMIN_TEST]: Number(billingDetails?.featureCreditBalances?.admin_test || billingDetails?.automationCreditsAvailable || 0),
});

export const isBundlePurchaseType = (bundles: any[], purchaseType?: string | null) =>
  bundles.some((item) => item.purchaseType === purchaseType);

export const getSubscriptionDisplayMeta = (
  subscriptionType: string,
  service: any,
  sharedPlanContentByType: Record<string, any> = SHARED_SUBSCRIPTION_PLAN_CONTENT_BY_TYPE,
) => {
  const sharedPlanContent = sharedPlanContentByType[subscriptionType];
  if (sharedPlanContent) {
    return {
      title: sharedPlanContent.title,
      subtitle: sharedPlanContent.subtitle,
    };
  }
  return {
    title: service?.name || "",
    subtitle: "",
  };
};

export const getSubscriptionCardContent = (
  subscriptionType: string,
  service: any,
  sharedPlanContentByType: Record<string, any> = SHARED_SUBSCRIPTION_PLAN_CONTENT_BY_TYPE,
) => {
  const sharedPlanContent = sharedPlanContentByType[subscriptionType];
  if (sharedPlanContent) {
    return sharedPlanContent;
  }
  return {
    description: service.description || "",
    benefits: service.includes || [],
    isCorePlan: false,
    isAddOnPlan: false,
    badgeLabel: "Plan",
  };
};

export const usesSingleAddSubscriptionControl = (subscriptionType: string) => (
  subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.NURSE_BASE_PLAN
  || subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.CAREGIVER_PLAN
  || subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.FAMILY_PLAN
  || subscriptionType === BillingConstants.SUBSCRIPTION_TYPE.BUSINESS_VENDOR_PLAN
);

export const getBillingTabIndexFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const hasOneTimeParam = String(params.get("oneTime") || "").trim().toLowerCase();
  const requestedTab = (params.get("tab") || params.get("billingTab") || "").trim().toLowerCase();

  if (["true", "1", "yes"].includes(hasOneTimeParam)) {
    return 1;
  }

  if (["nurse-addon", "nurse_addon", "nurseplanaddon", "nurse-plan-addon", "addon"].includes(requestedTab)) {
    return 2;
  }

  if (["credits", "credit", "automation-credits", "automation_credit", "one-time", "onetime", "one_time", "one-time-buy", "onetimes", "one"].includes(requestedTab)) {
    return 1;
  }

  if (["subscription", "subscriptions", "sub"].includes(requestedTab)) {
    return 0;
  }

  return 0;
};

export const normalizeBillingAddress = (address?: any) => ({
  firstName: String(address?.firstName || address?.billingFirstName || "").trim(),
  lastName: String(address?.lastName || address?.billingLastName || "").trim(),
  line1: String(address?.line1 || address?.addressLine1 || "").trim(),
  line2: String(address?.line2 || address?.addressLine2 || "").trim(),
  city: String(address?.city || "").trim(),
  state: String(address?.state || "WA").trim().toUpperCase(),
  postalCode: String(address?.postalCode || address?.postal_code || address?.zipcode || "").trim(),
  country: String(address?.country || "US").trim().toUpperCase(),
});

export const hasCompleteBillingAddress = (address?: any) => {
  const normalized = normalizeBillingAddress(address);
  return !!normalized.line1 && !!normalized.city && !!normalized.state && !!normalized.postalCode && !!normalized.country;
};
